import { useEffect, useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'

import { Layout } from '@loadsmart/loadsmart-ui'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { MainContent } from 'core/ui/MainContent'

import { type ConfirmAwardedLaneAction } from '../domain/ConfirmAwardedLaneAction'
import { getLanePerProposalPreference } from '../domain/Lane'
import {
  getAwardedLaneIds,
  isProposalAwardExpired,
  isProposalAwardFinished,
} from '../domain/Proposal'
import { AwardedEmptyState } from './AwardedEmptyState'
import { AwardedLanesList } from './AwardedLanesList'
import { AwardedProposalDetails } from './AwardedProposalDetails'
import { AwardedProposalProgress } from './AwardedProposalProgress'
import { ConfirmAwardDialog } from './ConfirmAwardDialog'
import { ErrorState } from './ErrorState'
import { useAwardedProposal } from './hooks/useAwardedProposal'

type AwardedProposalPageProps = {
  match: {
    params: {
      id: string
    }
  }
}

export function AwardedProposalPage({ match }: AwardedProposalPageProps): JSX.Element {
  const { id } = match.params
  const [isConfirmAwardDialogOpen, setIsConfirmAwardDialogOpen] = useState(false)

  const {
    awardedProposal,
    awardedProposalStatus,
    isConfirmingAllLanes,
    isConfirmingSingleLane,
    isSubmittingAwardConfirmation,
    isAwardAutoSubmitChecked,
    isAutoSubmittingAward,
    fetchAwardedProposal,
    refetchAwardedProposal,
    confirmAllLanes,
    confirmSingleLane,
    submitAwardConfirmation,
    toggleAwardAutoSubmit,
  } = useAwardedProposal()

  const isFinished = isProposalAwardFinished(awardedProposal)
  const isExpired = isProposalAwardExpired(awardedProposal)
  const isDisabled = isFinished || isExpired

  useEffect(() => {
    const abortController = new AbortController()

    fetchAwardedProposal(id, abortController)

    return () => {
      abortController.abort()
    }
  }, [id, fetchAwardedProposal])

  const awardedProposalWithPreferences = useMemo(() => {
    if (!awardedProposal) {
      return awardedProposal
    }

    return {
      ...awardedProposal,
      lanes: getLanePerProposalPreference(awardedProposal.lanes),
    }
  }, [awardedProposal])

  async function handleConfirmAll(action: ConfirmAwardedLaneAction) {
    const laneProposalIds = getAwardedLaneIds(awardedProposal!)

    try {
      await confirmAllLanes({
        rfpId: id,
        action,
        laneProposalIds,
      })
      await refetchAwardedProposal(id)
    } catch (error) {
      // does nothing
    }
  }

  async function handleConfirmLane(laneId: number, action: ConfirmAwardedLaneAction) {
    try {
      await confirmSingleLane({ rfpId: id, action, laneProposalIds: [laneId] })
      await refetchAwardedProposal(id)
    } catch (error) {
      /**
       * If the single lane confirmation fails, we need
       * to reload the page using the normal fetch,
       * due to the toggle buttons behavior
       */
      fetchAwardedProposal(id)
    }
  }

  async function handleSubmitAward() {
    try {
      await submitAwardConfirmation(id)
      await refetchAwardedProposal(id)
      setIsConfirmAwardDialogOpen(false)
    } catch (error) {
      // does nothing
    }
  }

  async function handleToggleAwardAutoSubmit() {
    try {
      await toggleAwardAutoSubmit(id)
    } catch (error) {
      // does nothing
    }
  }

  if (isFinished || isExpired) {
    return (
      <Redirect
        exact
        from={`/portal/rfp/${id}/award`}
        to={`/portal/rfp/${id}/award/confirmation`}
      />
    )
  }

  return (
    <MainContent>
      <AsyncContent
        status={awardedProposalStatus}
        loadingState={<LoadingState />}
        errorState={<ErrorState />}
        emptyState={<AwardedEmptyState />}
        count={awardedProposalWithPreferences?.lanes.length}
      >
        {awardedProposalWithPreferences && (
          <Layout.Stack>
            {isConfirmAwardDialogOpen && (
              <ConfirmAwardDialog
                rfpId={id}
                awardedProposal={awardedProposalWithPreferences}
                isSubmittingAwardConfirmation={isSubmittingAwardConfirmation}
                onConfirm={handleSubmitAward}
                onCancel={() => setIsConfirmAwardDialogOpen(false)}
              />
            )}
            <AwardedProposalDetails
              rfpId={id}
              awardedProposal={awardedProposalWithPreferences}
              isDisabled={isDisabled}
              isAwardAutoSubmitChecked={isAwardAutoSubmitChecked}
              isAutoSubmittingAward={isAutoSubmittingAward}
              onSubmit={() => setIsConfirmAwardDialogOpen(true)}
              onToggleAutoSubmit={handleToggleAwardAutoSubmit}
            />
            <AwardedProposalProgress
              awardedProposal={awardedProposalWithPreferences}
              isConfirmingAllLanes={isConfirmingAllLanes}
              isDisabled={isFinished}
              handleConfirmAll={handleConfirmAll}
            />
            <AwardedLanesList
              rfpId={id}
              awardedProposal={awardedProposalWithPreferences}
              handleConfirmLane={handleConfirmLane}
              isDisabled={isFinished}
              isConfirmingSingleLane={isConfirmingSingleLane}
            />
          </Layout.Stack>
        )}
      </AsyncContent>
    </MainContent>
  )
}
